export type ValidatorFn = (value: unknown) => boolean | string;
// Validator lists may contain direct boolean/string values alongside functions
// (e.g. pre-evaluated comparisons like `a === b || 'error message'`)
export type ValidatorList = Array<ValidatorFn | boolean | string>;
import type { SitecoreDictionary } from '@/lib/dictPlugin';

interface Fields {
  [fieldName: string]: { value?: string } | undefined;
}

// Options API component context used by the standalone exported functions
interface ComponentContext {
  password?: string;
  passwordRepeat?: string;
  passwordValid?: boolean;
  $dict: SitecoreDictionary;
}

export interface ValidatorsResult {
  required: ValidatorList;
  requiredAfterTrimmed: ValidatorList;
  requiredArray: ValidatorList;
  noTags: ValidatorList;
  zipcode: ValidatorList;
  firstname: ValidatorList;
  lastname: ValidatorList;
  isNumber: ValidatorList;
  fanclubNumber: ValidatorList;
  email: ValidatorList;
  emailTrimmed: ValidatorList;
  emailTrimmedWithoutRequired: ValidatorList;
  dateOfBirth: ValidatorList;
  emailAllowedCharacters: ValidatorList;
  phoneAllowedCharacters: ValidatorList;
  phone: ValidatorList;
  phoneRequired: ValidatorList;
  fax: ValidatorList;
  targetGroup: ValidatorList;
  craft: ValidatorList;
  retailer: ValidatorList;
  craftClassic: ValidatorList;
  retailerClassic: ValidatorList;
  street: ValidatorList;
  streetnumber: ValidatorList;
  city: ValidatorList;
  country: ValidatorList;
  countryClassic: ValidatorList;
  privacyProtection: ValidatorList;
  termsAndConditions: ValidatorList;
  transferredCart: ValidatorList;
  businessPartnerId: ValidatorList;
  gst: ValidatorList;
}

const regExEmail =
  /^[\w"!#$%&'*+/=?`{|}_~^À-ž-]+(?:\.[\w"!#$%&'*+/=?`{|}_~^À-ž-]+)*@(?:[\wÀ-ž-]+\.)+[\wÀ-ž]{2,}$/;

function checkPasswordRepeat(this: ComponentContext, fields: Fields): ValidatorList {
  const getField = (fieldName: string) => fields?.[fieldName]?.value ?? '';
  return [
    (v) => !!v || (getField('passwordFieldValidationMsgRepeat') ?? ''),
    this.password === this.passwordRepeat ||
      getField('passwordFieldValidationMsgNoMatch') ||
      this.$dict.get('online-Shop.Password.Match-Check'),
  ];
}

function checkPassword(this: ComponentContext, fields: Fields): ValidatorList {
  const getField = (fieldName: string) => fields?.[fieldName]?.value ?? '';
  return [
    (v) =>
      (this.passwordValid && !!v) ||
      getField('passwordFieldValidationMsgRequired') ||
      this.$dict.get('online-Shop.Global-Labels.Error-Message-Required'),
  ];
}

function validateCP(this: ComponentContext, value: string, regex: string): ValidatorList {
  const pattern = new RegExp(`^${regex}$`);
  return [
    pattern.test(value) || this.$dict.get('construction-Projects.Global-Labels.Invalid-Input'),
  ];
}

function validateZip(this: ComponentContext, value: string, regex: string): ValidatorList {
  if (regex && regex !== '' && value && value !== '') {
    const pattern = new RegExp(regex);
    return [
      pattern.test(value) || this.$dict.get('online-Shop.Global-Labels.Error-Message-Zipcode'),
    ];
  }
  return [
    (value && value !== '') || this.$dict.get('online-Shop.Global-Labels.Error-Message-Required'),
  ];
}

export { checkPasswordRepeat, checkPassword, validateZip, validateCP };

export default function Validators(
  $dict: SitecoreDictionary,
  fields: Fields | null,
): ValidatorsResult {
  const getField = (fieldName: string) => fields?.[fieldName]?.value ?? '';

  const noTrailingAndLeadingWhitespaces = (value: string) => {
    const pattern = /^\s*$/;
    return !pattern.test(value);
  };

  const noTags = (value: string) => {
    const pattern = /<([a-zA-Z0-9]+)(?:[^.]*)(?:[^>][^/]*)?>/g;
    return !pattern.test(value) || $dict.get('online-Shop.Global-Labels.Invalid-Input');
  };

  const required: ValidatorList = [
    (value) =>
      ((value && noTags(value as string) && noTrailingAndLeadingWhitespaces(value as string)) ||
        $dict.get('online-Shop.Global-Labels.Error-Message-Required')) as boolean | string,
  ];

  const requiredAfterTrimmed: ValidatorList = [
    (value) =>
      ((value && noTags(value as string)) ||
        $dict.get('online-Shop.Global-Labels.Error-Message-Required')) as boolean | string,
  ];

  const zipSitecore = $dict.get('global-Settings.Zip-Code-RegEx');

  const phone: ValidatorList = [
    (value) => {
      const pattern = /^[0-9+)(/\\\s-]*$/;
      return (pattern.test(value as string) ||
        getField('phoneNumberValidationMessage') ||
        $dict.get('online-Shop.Global-Labels.Error-Message-Phone-Literal-Error')) as
        | boolean
        | string;
    },
  ];

  const fax: ValidatorList = [
    (value) => {
      const pattern = /^[0-9+)(/\\\s-]*$/;
      return (pattern.test(value as string) ||
        getField('faxNumberValidationMessage') ||
        $dict.get('online-Shop.Global-Labels.Invalid-Input')) as boolean | string;
    },
  ];

  let zipRegEx: RegExp = /^\d{5}$/;
  if (zipSitecore && zipSitecore !== '') {
    zipRegEx = new RegExp(zipSitecore);
  }

  return {
    required,
    requiredAfterTrimmed,
    requiredArray: [
      (value: unknown) =>
        ((value as unknown[])?.length && noTags(value as string)) ||
        $dict.get('online-Shop.Global-Labels.Error-Message-Required'),
    ],
    noTags: [
      (value: unknown) =>
        noTags(value as string) || $dict.get('online-Shop.Global-Labels.Invalid-Input'),
    ],
    zipcode: [
      (value: unknown) =>
        ((value && zipRegEx.test(value as string)) ||
          getField('zipCodeValidationMessage') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Zipcode')) as boolean | string,
    ],
    firstname: [
      (value: unknown) =>
        ((value && noTags(value as string)) ||
          getField('firstNameValidationMessage') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Required')) as boolean | string,
    ],
    lastname: [
      (value: unknown) =>
        ((value && noTags(value as string)) ||
          getField('lastNameValidationMessage') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Required')) as boolean | string,
    ],
    isNumber: [(value: unknown) => /^\d+$/.test(value as string)],
    fanclubNumber: [
      ...required,
      (value: unknown) => {
        const pattern = /^\d{4,6}$/;
        return pattern.test(value as string) || getField('fanclubnumberValidationMsgInvalidFormat');
      },
    ],
    email: [
      ...required,
      (value: unknown) => {
        const pattern = regExEmail;
        return (
          pattern.test(value as string) ||
          getField('emailFieldValidationMsgInvalidFormat') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Email-Literal-Error')
        );
      },
    ],
    emailTrimmed: [
      ...required,
      (value: unknown) => {
        const emailTrimmed = (value as string).trim();
        const pattern = regExEmail;
        return (
          pattern.test(emailTrimmed) ||
          getField('emailFieldValidationMsgInvalidFormat') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Email-Literal-Error')
        );
      },
    ],
    emailTrimmedWithoutRequired: [
      (value: unknown) => {
        if (value === null || value === '') {
          return true;
        }
        const emailTrimmed = (value as string).trim();
        const pattern =
          /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}$/;
        return (
          pattern.test(emailTrimmed) ||
          getField('emailFieldValidationMsgInvalidFormat') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Email-Literal-Error')
        );
      },
    ],
    dateOfBirth: [...required],
    emailAllowedCharacters: [
      (value: unknown) => /^[\w!#$%&'*+/=?`{|}~^@.?-]+$/.test(value as string),
    ],
    phoneAllowedCharacters: [(value: unknown) => /^[0-9+)(/\\\s-]*$/.test(value as string)],
    phone: [...phone],
    phoneRequired: [...required, ...phone],
    fax: [...fax],
    targetGroup: [(value: unknown) => !!value || getField('targetGroupValidationMessage')],
    craft: [(value: unknown) => !!value || getField('craftValidationMessage')],
    retailer: [(value: unknown) => !!value || getField('retailerValidationMessage')],
    craftClassic: [
      (value: unknown) =>
        ((value && !!(value as { value?: unknown }).value) ||
          getField('craftValidationMessage')) as boolean | string,
    ],
    retailerClassic: [
      (value: unknown) =>
        ((value && !!(value as { value?: unknown }).value) ||
          getField('retailerValidationMessage')) as boolean | string,
    ],
    street: [
      (value: unknown) =>
        ((value && noTags(value as string)) ||
          getField('streetValidationMessage') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Required')) as boolean | string,
    ],
    streetnumber: [
      (value: unknown) =>
        ((value && noTags(value as string)) ||
          getField('streetNumberValidationMessage') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Required')) as boolean | string,
    ],
    city: [
      (value: unknown) =>
        ((value && noTags(value as string)) ||
          getField('cityValidationMessage') ||
          $dict.get('online-Shop.Global-Labels.Error-Message-Required')) as boolean | string,
    ],
    country: [
      (value: unknown) =>
        !!value ||
        getField('countryValidationMessage') ||
        $dict.get('online-Shop.Global-Labels.Error-Message-Required'),
    ],
    countryClassic: [
      (value: unknown) =>
        ((value && !!(value as { value?: unknown }).value) ||
          getField('countryValidationMessage')) as boolean | string,
    ],
    privacyProtection: [(value: unknown) => !!value || getField('dataPrivacyValidationMessage')],
    termsAndConditions: [
      (value: unknown) => !!value || getField('termsAndConditionsValidationMessage'),
    ],
    transferredCart: [(value: unknown) => !!value || getField('transferredCartValidationMessage')],
    businessPartnerId: [
      (value: unknown) =>
        value === '' ||
        /^\d+$/.test(value as string) ||
        $dict.get('online-Shop.Global-Labels.Error-Message-Business-Partner-ID'),
    ],
    gst: [
      (value: unknown) =>
        value === '' ||
        /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value as string) ||
        $dict.get('online-Shop.Global-Labels.Invalid-Input'),
    ],
  };
}
